import { useState } from 'react';

interface FAQItem {
  question: string;
  answer: string;
  icon: string;
}

const faqs: FAQItem[] = [
  {
    icon: 'ri-server-line',
    question: '為什麼有導入費跟保固費？',
    answer:
      '導入費（NT$ 48,000）是為您梳理流程、設定專屬模組與 API 串接的「系統架構費」；每月 NT$ 3,000 的保固費則涵蓋了企業級雲端伺服器的運行成本、數據安全防護與技術支援。這筆費用是維持系統專業運作的必要成本，而非額外收費項目。',
  },
  {
    icon: 'ri-shield-check-line',
    question: '這套 O2O 動態展間系統的穩定度與安全性如何？',
    answer:
      '全鏈行銷採用國際企業級雲端架構，提供前後端分離的獨立 Web App 展間。不僅確保極致的行動端載入速度與防呆管理機制，更具備銀行級的資料安全防護，讓您的商業數據獲得最高規格的保障。',
  },
  {
    icon: 'ri-shield-flash-line',
    question: '廣告合規審核真的重要嗎？',
    answer:
      '極度重要。我們具備高度政策敏銳度，主動過濾高風險用詞，確保廣告符合平台規範，保護您的品牌粉絲專頁免受停權與客訴風險。使用高風險詞彙，輕則廣告遭拒刊，重則導致整個粉絲專頁被停權，嚴重影響企業線上業績。',
  },
];

interface HomeFAQSectionProps {
  onCTAClick?: () => void;
}

export default function HomeFAQSection({ onCTAClick }: HomeFAQSectionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-20 sm:py-28 bg-[#060F1E] relative overflow-hidden">
      {/* 背景裝飾 */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/3 left-1/4 w-[350px] h-[350px] bg-[#00D4FF]/4 rounded-full blur-[100px]"></div>
        <div className="absolute bottom-1/4 right-1/4 w-[300px] h-[300px] bg-[#D4AF37]/4 rounded-full blur-[100px]"></div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 relative z-10">
        {/* 標題 */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-[#00D4FF]/10 border border-[#00D4FF]/30 text-[#00D4FF] px-5 py-2 rounded-full text-xs sm:text-sm font-semibold uppercase tracking-widest mb-6">
            <i className="ri-question-answer-line"></i>
            <span>常見問題 FAQ</span>
          </div>
          <h2 className="text-2xl sm:text-4xl font-bold text-white mb-4">
            企業主最常問的
            <span className="block mt-1 bg-gradient-to-r from-[#00D4FF] to-[#D4AF37] bg-clip-text text-transparent">
              三大關鍵問題
            </span>
          </h2>
          <p className="text-gray-400 text-sm sm:text-base max-w-xl mx-auto leading-relaxed">
            在合作前，我們希望您完全清楚每一分錢的去向與每一個決策的原因。
          </p>
        </div>

        {/* FAQ 手風琴 */}
        <div className="space-y-4 mb-12">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className={`border rounded-2xl overflow-hidden transition-all duration-300 ${
                openIndex === index
                  ? 'border-[#D4AF37]/50 bg-gradient-to-b from-[#1A1200] to-[#0D1A2A]'
                  : 'border-[#1E3A5A] bg-[#0D1F35]'
              }`}
            >
              <button
                onClick={() => toggle(index)}
                className="w-full px-6 sm:px-8 py-5 flex items-center gap-4 text-left cursor-pointer"
              >
                <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 transition-all ${
                  openIndex === index
                    ? 'bg-gradient-to-br from-[#D4AF37] to-[#F0C040]'
                    : 'bg-[#00D4FF]/15'
                }`}>
                  <i className={`${faq.icon} text-lg ${openIndex === index ? 'text-[#060F1E]' : 'text-[#00D4FF]'}`}></i>
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-white flex-1 pr-4">
                  {faq.question}
                </h3>
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <i className={`ri-arrow-down-s-line text-2xl transition-transform duration-300 ${
                    openIndex === index ? 'rotate-180 text-[#D4AF37]' : 'text-gray-500'
                  }`}></i>
                </div>
              </button>

              <div className={`overflow-hidden transition-all duration-400 ${
                openIndex === index ? 'max-h-96' : 'max-h-0'
              }`}>
                <div className="px-6 sm:px-8 pb-6 border-t border-[#1E3A5A]/50">
                  <p className="text-gray-300 text-sm sm:text-base leading-relaxed pt-5">
                    {faq.answer}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 底部 CTA */}
        {onCTAClick && (
          <div className="text-center">
            <p className="text-gray-400 text-sm mb-5">還有其他問題？我們的顧問 24H 內回覆</p>
            <button
              onClick={onCTAClick}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-[#D4AF37] to-[#F0C040] text-[#060F1E] px-8 py-3.5 rounded-full font-bold text-sm hover:shadow-[0_4px_20px_rgba(212,175,55,0.5)] transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-calendar-check-line"></i>
              預約免費數位診斷，現場解答您的問題
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
